import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CompanyUser } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateCompanyUserCache } from './company-cache';

export type FetchCompanyUserResult = {
    status: 'error' | 'success' | 'pending';
    user: CompanyUser | undefined;
    /** Why the read failed, ready to show. Empty while it has not. */
    error: string;
    /** False when retrying cannot help — the member is gone, or refused. */
    canRetry: boolean;
    refetch: () => void;
};

export function useFetchCompanyUser(
    companyUserId: string
): FetchCompanyUserResult {
    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so every screen reading
    // this member says the same thing.
    const describeError = useCallback(
        (error: unknown): { message: string; canRetry: boolean } => {
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return {
                    message: t('shared.errors.member_load_not_found'),
                    canRetry: false
                };
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return {
                    message: t('shared.errors.member_load_not_found'),
                    canRetry: false
                };
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return { message: t('shared.errors.generic'), canRetry: false };
            }

            // Anything else — a dropped connection, a 500 — may well work on a
            // second press.
            return { message: t(getFallbackErrorKey(error)), canRetry: true };
        },
        [t]
    );

    const {
        status,
        data: user,
        refetch,
        error
    } = useQuery({
        queryKey: ['fetchCompanyUser', companyUserId],
        queryFn: () => {
            return openApiRef.current.users
                .getUser(companyUserId)
                .then((response) => {
                    const user = response.data.data.user;
                    addOrUpdateCompanyUserCache(queryClient, user);
                    return user;
                });
        }
    });

    const described = error ? describeError(error) : null;

    return {
        status,
        user,
        error: described?.message ?? '',
        canRetry: described?.canRetry ?? true,
        refetch: () => {
            void refetch();
        }
    };
}

// Re-exported so existing import sites keep working; the constants live in
// `company-query-keys` to keep the two service modules free of an import cycle.
